import type { ReactElement } from 'react';
import { useState } from 'react';

import { get, del } from 'utils/network';
import { ApiRoute } from 'config';

import type Address from 'models/Address';
import type IApiResponse from 'models/IApiResponse';
import type { INotification } from 'models/Notifications';
import type { IUpcomingMenu } from 'models/UpcomingMenu';
import { NotificationType } from 'models/Notifications';

import useNotifications from 'hooks/useNotifications';
import useNextDeliveryDate from 'hooks/useNextDeliveryDate';

import AddressModal from 'components/shared/modals/AddressModal';

type Props = {
  address: Address;
  onUpdate: (address: Address) => void;
  onDelete: (address: Address) => void;
};

function DeliveryAddressCard({ address, onUpdate, onDelete }: Props): ReactElement {
  const [showAddressModal, setShowAddressModal] = useState(false);
  const getNextDeliveryDate = useNextDeliveryDate();
  const { dispatch } = useNotifications();

  const deleteAddress = async () => {
    const response = await del<IApiResponse[]>(
      `${ApiRoute.SHIPPING_ADDRESSES}/${address.id}`,
    );

    if (response.ok) {
      onDelete(address);

      dispatch({
        type: 'addNotification',
        notification: {
          id: address.id,
          notification_type: NotificationType.ADDRESS_CHANGE,
          operation: 'REMOVE',
        } as INotification,
      });
    }
  };

  const closeAddressModal = async (updatedAddress) => {
    if (updatedAddress) {
      const { primary, id } = updatedAddress;
      let nextDeliveryDate = null;

      if (primary) {
        const response = await get<IUpcomingMenu[]>(ApiRoute.UPCOMING_MENUS);
        // @ts-expect-error TODO: fix null safety
        nextDeliveryDate = getNextDeliveryDate(response.parsedBody, id);
      }

      dispatch({
        type: 'addNotification',
        notification: {
          id: updatedAddress.id,
          notification_type: NotificationType.ADDRESS_CHANGE,
          date: nextDeliveryDate,
          address: primary && updatedAddress,
          operation: 'UPDATE',
        } as INotification,
      });

      onUpdate(updatedAddress);
    }

    setShowAddressModal(false);
  };

  return (
    <div className="col-12 col-sm-6 col-lg-4 c-profile__address-item">
      <div
        className={[
          'c-profile__address-card',
          address.isValid ? '' : 'is-invalid',
        ].join(' ')}
      >
        <div className="c-profile__address-card-content">
          {address.primary && (
            <h2 className="c-profile__address-card-heading">
              Primary Address
            </h2>
          )}

          <span>{address.fullName}</span>
          <br />
          <span>{`${address.firstLine} ${address.secondLine}`}</span>
          <br />
          <span>{address.lastLine}</span>
          <br />
          <span>{`Tel: ${address.local_phone_number}`}</span>
          <br />

          <div className="c-profile__address-card-actions">
            <button
              type="button"
              onClick={() => setShowAddressModal(true)}
              className="c-link u-text--sm"
              aria-label="Edit account settings"
            >
              Edit
            </button>

            {!address.primary && (
              <button
                type="button"
                onClick={() => deleteAddress()}
                className="c-profile__delete-link c-link u-text--sm"
                aria-label="Edit account settings"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {showAddressModal && (
        <AddressModal
          isPrimaryEditable
          isAccountManagementPage
          initialAddress={address}
          onClose={closeAddressModal}
        />
      )}
    </div>
  );
}

export default DeliveryAddressCard;
